import { type FC, useEffect, useId } from 'react';
import {
  type Transition,
  motion,
  useSpring,
  useTransform,
  motionValue,
} from 'motion/react';
import { default as useMeasure } from 'react-use-measure';
import { Assert } from '@team/assert';
import { NumberKit } from '@team/number';
import { StringKit } from '@team/string';
import type { DigitProps, EntryProps, NumberProps } from './types';
import css from './style.module.css';

const TRANSITION = {
  type: 'spring',
  stiffness: 280,
  damping: 18,
  mass: 0.3,
} satisfies Transition;

export const Entry: FC<EntryProps> = ({
  value,
  padStart = false,
  decimalSeparator = '.',
}) => {
  const absValue = Math.abs(value);
  const [integerPart, decimalPart] = absValue.toString().split('.');
  const integerValue = parseInt(integerPart, 10);
  const paddedInteger = padStart
    ? NumberKit.padZeroStart(integerValue)
    : integerPart;
  const integerDigits = paddedInteger.split('');
  const integerPlaces = integerDigits.map((_, i) =>
    Math.pow(10, integerDigits.length - i - 1)
  );

  const hasMinusSign = NumberKit.isNegative(value);
  const hasDecimalPart =
    Assert.isString(decimalPart) && StringKit.isNotEmpty(decimalPart);

  return (
    <span className={css.root}>
      {hasMinusSign && '-'}

      {integerDigits.map((_, index) => (
        <Digit
          key={`pos-${integerPlaces[index]}`}
          value={integerValue}
          place={integerPlaces[index]}
        />
      ))}

      {hasDecimalPart && (
        <>
          <span>{decimalSeparator}</span>

          {decimalPart.split('').map((_, index) => (
            <Digit
              key={`decimal-${index}`}
              value={parseInt(decimalPart, 10)}
              place={Math.pow(10, decimalPart.length - index - 1)}
            />
          ))}
        </>
      )}
    </span>
  );
};

const Digit: FC<DigitProps> = ({ value, place }) => {
  const valueRoundedToPlace = Math.floor(value / place) % 10;
  const initial = motionValue(valueRoundedToPlace);
  const animatedValue = useSpring(initial, TRANSITION);

  useEffect(() => {
    animatedValue.set(valueRoundedToPlace);
  }, [animatedValue, valueRoundedToPlace]);

  return (
    <span className={css.digit}>
      <span className={css.invisible}>0</span>

      {Array.from({ length: 10 }, (_, i) => (
        <Number key={i} mv={animatedValue} number={i} />
      ))}
    </span>
  );
};

const Number: FC<NumberProps> = ({ mv, number }) => {
  const uniqueId = useId();
  const [ref, bounds] = useMeasure();

  const y = useTransform(mv, (latest) => {
    if (!NumberKit.isPositive(bounds.height)) {
      return 0;
    }

    const placeValue = latest % 10;
    const offset = (10 + number - placeValue) % 10;
    let memo = offset * bounds.height;

    if (offset > 5) {
      memo -= 10 * bounds.height;
    }

    return memo;
  });

  if (!NumberKit.isPositive(bounds.height)) {
    return (
      <span ref={ref} className={css.placeholder}>
        {number}
      </span>
    );
  }

  return (
    <motion.span
      className={css.number}
      layoutId={`${uniqueId}-${number}`}
      transition={TRANSITION}
      style={{ y }}
      ref={ref}
    >
      {number}
    </motion.span>
  );
};
